/**
 * Admin Island Alerts page — broadcast safety & service notifications
 * to the whole rider/driver base.
 *
 * The operator types the EXACT title + message (free-form, with live
 * character counters and a phone-style preview), picks a severity tier
 * and an audience, and sends. The server (`send_island_alert` RPC,
 * migration 20260721140000, ceiling raised in 20260721160000) enforces
 * the admin gate, validation, and a 30-per-hour security backstop, then
 * fans out Expo pushes and lights up the in-app banners over realtime.
 *
 * Sent alerts are IMMUTABLE audit rows — there is deliberately no edit:
 * pushes can't be recalled, so editing would only falsify the record of
 * what was actually sent. "Reuse" loads a past alert into the composer
 * to tweak and send as a NEW alert (fresh audit row).
 *
 * Severity tiers (mirror the mobile app's banner styling):
 *   • Notice    — service info. Respects each user's local waking hours.
 *   • Advisory  — weather watch / road closure. Sends immediately, any hour.
 *   • Emergency — hurricane warning / major hazard. Sends immediately,
 *                 loudest channel, pulsing red banner in-app. Requires
 *                 typing SEND to confirm — this wakes the island up.
 *
 * Audience — and what it does NOT control:
 *   • On island — profiles whose device was last seen inside Dominica
 *                 (coarse boolean flag, ≤14 days old).
 *   • Everyone  — every install with a push token, worldwide.
 *
 * The audience selects PUSH recipients only. The in-app banner
 * (`get_active_island_alerts`) deliberately ignores it and shows to
 * anyone who opens the app, because the on-island flag is blank for
 * users who never granted location and stale after 14 days without an
 * app open — and those users may be in the storm's path. The composer
 * says this in plain language, because an operator who tests an
 * on-island alert from off-island receives nothing and reasonably
 * concludes the pipeline is broken. `admin_send_island_alert_self_test`
 * (migration 20260817200000) is the way to prove otherwise: one push to
 * the operator's own device, on the quiet `island_updates` channel, with
 * no row written to the immutable `island_alerts` audit table.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Ban,
  BellOff,
  FlaskConical,
  Info,
  MapPin,
  Megaphone,
  RotateCcw,
  Send,
  Siren,
  SmilePlus,
  Globe,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

type Severity = 'info' | 'advisory' | 'emergency';
type Audience = 'on_island' | 'everyone';

/**
 * The signed-in operator's own delivery state, from
 * `admin_island_alert_self_status`. `on_island` is evaluated with the same
 * predicate the push fan-out uses, so it answers "will this alert reach me?"
 * exactly.
 */
interface SelfStatus {
  on_island: boolean;
  last_seen_on_island_at: string | null;
  has_push_token: boolean;
  last_test_at: string | null;
}

interface AlertRow {
  id: string;
  severity: Severity;
  audience: Audience;
  title: string;
  body: string;
  created_at: string;
  expires_at: string;
  canceled_at: string | null;
  push_recipients: number;
  sent_by_name: string | null;
}

const TITLE_MAX = 80;
const BODY_MAX = 400;

const SEVERITIES: {
  value: Severity;
  label: string;
  desc: string;
  icon: typeof Info;
  selectedClass: string;
}[] = [
  {
    value: 'info',
    label: 'Notice',
    desc: 'Service info. Delivered during each user\u2019s waking hours.',
    icon: Info,
    selectedClass: 'border-emerald-500 bg-emerald-500/10',
  },
  {
    value: 'advisory',
    label: 'Advisory',
    desc: 'Weather watch, road closure. Sends immediately, any hour.',
    icon: AlertTriangle,
    selectedClass: 'border-amber-500 bg-amber-500/10',
  },
  {
    value: 'emergency',
    label: 'Emergency',
    desc: 'Hurricane warning, major hazard. Loudest delivery, wakes people.',
    icon: Siren,
    selectedClass: 'border-red-500 bg-red-500/10',
  },
];

const TEMPLATES: { label: string; severity: Severity; title: string; body: string }[] = [
  {
    label: 'Storm advisory',
    severity: 'advisory',
    title: 'Storm approaching Dominica',
    body: 'A storm system is expected to affect the island later today. Plan your trips early: bus service may pause when conditions worsen. Stay safe.',
  },
  {
    label: 'Hurricane warning',
    severity: 'emergency',
    title: 'Hurricane warning: seek shelter',
    body: 'A hurricane warning is in effect for Dominica. All bus service is suspended. Secure your home, stay indoors, and follow official guidance from the Office of Disaster Management.',
  },
  {
    label: 'Road hazard',
    severity: 'advisory',
    title: 'Major road hazard',
    body: 'A landslide is blocking the road. Expect delays and detours. Drive carefully and follow directions from authorities on site.',
  },
  {
    label: 'Service notice',
    severity: 'info',
    title: 'Service update',
    body: 'Bus service will be limited on the upcoming public holiday. Check the map for live buses before heading out.',
  },
];

/**
 * Curated emoji palette for alert copy — weather, hazards, transit,
 * guidance, and event vibes. A focused grid beats a full emoji picker
 * here: operators find the right symbol in one glance, and every entry
 * renders reliably in push notifications on both platforms.
 */
const EMOJI_GROUPS: { label: string; emojis: string[] }[] = [
  { label: 'Weather', emojis: ['🌀', '🌪️', '⛈️', '🌧️', '💨', '🌊', '🌩️', '☀️', '🌋', '💧'] },
  { label: 'Hazards', emojis: ['🚨', '⚠️', '🚧', '🛑', '⛔', '🔥', '🏥', '🚑', '🆘', '📵'] },
  { label: 'Transit', emojis: ['🚌', '🚍', '🚐', '🛣️', '📍', '🗺️', '⏰', '🕗', '⛽', '🌉'] },
  { label: 'General', emojis: ['📢', '📣', 'ℹ️', '✅', '❌', '❗', '🙏', '💚', '🏝️', '👀', '🎉', '🎶', '🥳', '🎟️', '🍽️', '⚡'] },
];

function severityBadge(s: Severity) {
  if (s === 'emergency') {
    return <Badge className="bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/30">Emergency</Badge>;
  }
  if (s === 'advisory') {
    return <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/30">Advisory</Badge>;
  }
  return <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30">Notice</Badge>;
}

/** Phone-style live preview of the push as the operator types. */
function PushPreview({ severity, title, body }: { severity: Severity; title: string; body: string }) {
  const prefix = severity === 'emergency' ? '🚨 ' : severity === 'advisory' ? '⚠️ ' : '';
  return (
    <div className="rounded-2xl border bg-gray-100 dark:bg-gray-800/60 p-3 shadow-inner">
      <div className="rounded-xl bg-white dark:bg-gray-900 border p-3 shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-5 h-5 rounded-md bg-emerald-500 flex items-center justify-center text-white text-[10px] font-black">
            H
          </div>
          <span className="text-xs font-semibold text-gray-500">H-Plate</span>
          <span className="text-[10px] text-gray-400 ml-auto">now</span>
        </div>
        <p className="text-sm font-bold leading-snug">
          {prefix}
          {title.trim() || 'Alert title…'}
        </p>
        <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 whitespace-pre-wrap">
          {body.trim() || 'Alert message…'}
        </p>
      </div>
      <p className="text-[10px] text-gray-500 mt-2 text-center">
        Lock-screen preview. In-app users also see a color-coded banner instantly.
      </p>
    </div>
  );
}

export default function IslandAlerts() {
  const [severity, setSeverity] = useState<Severity>('advisory');
  const [audience, setAudience] = useState<Audience>('on_island');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [ttlHours, setTtlHours] = useState(12);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [sending, setSending] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const [rows, setRows] = useState<AlertRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [selfStatus, setSelfStatus] = useState<SelfStatus | null>(null);
  const [testing, setTesting] = useState(false);

  const canSend = title.trim().length > 0 && body.trim().length > 0 && !sending;
  const needsTypedConfirm = severity === 'emergency';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('admin_list_island_alerts', {
        p_limit: 50,
        p_offset: 0,
      });
      if (error) throw error;
      const result = data as { ok: boolean; alerts?: AlertRow[]; error?: string } | null;
      if (!result?.ok) throw new Error(result?.error ?? 'Failed to load alerts');
      setRows(result.alerts ?? []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load alert history');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch the operator's own on-island state and token registration. Silent on
   * failure: not knowing is a reason to omit the warning, not to block sending
   * a storm warning.
   */
  const loadSelfStatus = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('admin_island_alert_self_status');
      if (error) throw error;
      const result = data as ({ ok: boolean } & Partial<SelfStatus>) | null;
      if (!result?.ok) return;
      setSelfStatus({
        on_island: result.on_island === true,
        last_seen_on_island_at: result.last_seen_on_island_at ?? null,
        has_push_token: result.has_push_token === true,
        last_test_at: result.last_test_at ?? null,
      });
    } catch {
      setSelfStatus(null);
    }
  }, []);

  useEffect(() => {
    void load();
    void loadSelfStatus();
  }, [load, loadSelfStatus]);

  /**
   * Send a test push to the operator's own device only. Bypasses the audience
   * (that is the point — an operator off the island is correctly excluded from
   * their own `on_island` broadcast) but reaches nobody else: the RPC selects
   * tokens by `auth.uid()` and takes no argument that names a user.
   */
  const sendSelfTest = useCallback(async () => {
    setTesting(true);
    try {
      const { data, error } = await supabase.rpc('admin_send_island_alert_self_test', {
        p_title: title.trim() || null,
        p_body: body.trim() || null,
      });
      if (error) throw error;
      const result = data as { ok: boolean; devices?: number; error?: string } | null;
      if (!result?.ok) {
        if (result?.error === 'no_push_token') {
          toast.error(
            'No push token registered for your account. Open the H-Plate app on your phone, sign in with this same account, and allow notifications. Until then no push of any kind can reach you.',
            { duration: 12000 },
          );
        } else if (result?.error === 'rate_limited') {
          toast.error('Ten tests in the last hour is the cap. Wait a few minutes.');
        } else {
          throw new Error(result?.error ?? 'unknown error');
        }
        return;
      }
      const devices = result.devices ?? 0;
      toast.success(
        `Test sent to your ${devices} registered device${devices === 1 ? '' : 's'}. ` +
          'If nothing arrives within a minute, notifications are blocked for H-Plate on that phone ' +
          'or its token is stale. Reopen the app and allow notifications.',
        { duration: 12000 },
      );
      await loadSelfStatus();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to send the test');
    } finally {
      setTesting(false);
    }
  }, [title, body, loadSelfStatus]);

  const doSend = useCallback(async () => {
    setSending(true);
    try {
      const { data, error } = await supabase.rpc('send_island_alert', {
        p_severity: severity,
        p_audience: audience,
        p_title: title.trim(),
        p_body: body.trim(),
        p_ttl_hours: ttlHours,
      });
      if (error) throw error;
      const result = data as { ok: boolean; recipients?: number; error?: string } | null;
      if (!result?.ok) {
        const msg =
          result?.error === 'rate_limited'
            ? 'Safety ceiling reached (30 alerts in the last hour). This exists to stop a compromised account from spamming the island. If this is really you, wait a few minutes.'
            : `Send failed: ${result?.error ?? 'unknown error'}`;
        throw new Error(msg);
      }
      toast.success(`Alert sent to ${result.recipients ?? 0} device${result.recipients === 1 ? '' : 's'}`);
      setTitle('');
      setBody('');
      setConfirmOpen(false);
      setConfirmText('');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to send alert');
    } finally {
      setSending(false);
    }
  }, [severity, audience, title, body, ttlHours, load]);

  /** Insert an emoji at the cursor position in the message box. */
  const insertEmoji = useCallback((emoji: string) => {
    const el = bodyRef.current;
    setBody((prev) => {
      const start = el?.selectionStart ?? prev.length;
      const end = el?.selectionEnd ?? prev.length;
      const next = prev.slice(0, start) + emoji + prev.slice(end);
      if (next.length > BODY_MAX) return prev;
      // Restore focus + caret right after the inserted emoji.
      requestAnimationFrame(() => {
        if (!el) return;
        el.focus();
        const pos = start + emoji.length;
        el.setSelectionRange(pos, pos);
      });
      return next;
    });
  }, []);

  /**
   * Load a past alert into the composer. Deliberately NOT an in-place
   * edit — sent alerts are immutable audit rows (see file header).
   */
  const reuseAlert = useCallback((r: AlertRow) => {
    setSeverity(r.severity);
    setAudience(r.audience);
    setTitle(r.title);
    setBody(r.body);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    toast.success('Loaded into the composer. Edit as needed, then send as a new alert.');
  }, []);

  const cancelAlert = useCallback(
    async (id: string) => {
      setBusyId(id);
      try {
        const { data, error } = await supabase.rpc('cancel_island_alert', { p_id: id });
        if (error) throw error;
        const result = data as { ok: boolean; error?: string } | null;
        if (!result?.ok) throw new Error(result?.error ?? 'Failed to cancel');
        toast.success('Alert canceled. In-app banners are hidden. Delivered pushes cannot be recalled.');
        await load();
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Failed to cancel alert');
      } finally {
        setBusyId(null);
      }
    },
    [load],
  );

  const audienceLabel = audience === 'on_island' ? 'people on Dominica' : 'everyone with the app';

  // The trap this page used to spring: an operator abroad picks "On Dominica",
  // is correctly excluded from their own push, and concludes alerts are broken.
  const outsideOwnAudience = audience === 'on_island' && selfStatus?.on_island === false;
  const lastSeenOnIsland = selfStatus?.last_seen_on_island_at
    ? formatDistanceToNow(new Date(selfStatus.last_seen_on_island_at), { addSuffix: true })
    : null;

  const statusOf = useMemo(
    () => (r: AlertRow) => {
      if (r.canceled_at) {
        return <Badge variant="outline" className="border-gray-500/30 text-gray-500">Canceled</Badge>;
      }
      if (new Date(r.expires_at).getTime() <= Date.now()) {
        return <Badge variant="outline" className="border-gray-500/30 text-gray-500">Expired</Badge>;
      }
      return <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30">Active</Badge>;
    },
    [],
  );

  return (
    <div className="p-6 space-y-6">
      <header>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Megaphone className="w-6 h-6 text-red-500" />
          Island Alerts
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Broadcast safety and service notifications to riders and drivers: storm
          warnings, major hazards, service disruptions. Pushes reach phones even
          with the app closed; open apps show a banner instantly.
        </p>
      </header>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
        {/* ── Compose ── */}
        <Card>
          <CardContent className="p-5 space-y-5">
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-2">1 · Severity</p>
              <div className="grid sm:grid-cols-3 gap-2">
                {SEVERITIES.map((s) => {
                  const Icon = s.icon;
                  const selected = severity === s.value;
                  return (
                    <button
                      key={s.value}
                      onClick={() => setSeverity(s.value)}
                      className={`text-left rounded-lg border-2 p-3 transition-colors ${
                        selected
                          ? s.selectedClass
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-400'
                      }`}
                    >
                      <div className="flex items-center gap-1.5 font-semibold text-sm">
                        <Icon className="w-4 h-4" />
                        {s.label}
                      </div>
                      <p className="text-xs text-gray-500 mt-1 leading-snug">{s.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 mb-2">2 · Who receives it</p>
              <div className="grid sm:grid-cols-2 gap-2">
                <button
                  onClick={() => setAudience('on_island')}
                  className={`text-left rounded-lg border-2 p-3 transition-colors ${
                    audience === 'on_island'
                      ? 'border-emerald-500 bg-emerald-500/10'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-center gap-1.5 font-semibold text-sm">
                    <MapPin className="w-4 h-4" />
                    On Dominica
                  </div>
                  <p className="text-xs text-gray-500 mt-1 leading-snug">
                    Users whose phone was last seen on the island (within 14 days).
                  </p>
                </button>
                <button
                  onClick={() => setAudience('everyone')}
                  className={`text-left rounded-lg border-2 p-3 transition-colors ${
                    audience === 'everyone'
                      ? 'border-emerald-500 bg-emerald-500/10'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-400'
                  }`}
                >
                  <div className="flex items-center gap-1.5 font-semibold text-sm">
                    <Globe className="w-4 h-4" />
                    Everyone
                  </div>
                  <p className="text-xs text-gray-500 mt-1 leading-snug">
                    Every install with notifications enabled, worldwide.
                  </p>
                </button>
              </div>

              <div className="mt-2 rounded-lg border bg-gray-50 dark:bg-gray-900/50 p-3">
                <p className="text-xs font-semibold flex items-center gap-1.5">
                  <Info className="w-3.5 h-3.5" />
                  This choice decides who gets the phone notification, nothing else.
                </p>
                <p className="text-[11px] text-gray-500 mt-1.5 leading-relaxed">
                  The in-app banner is shown to <strong>anyone who opens the app</strong>,
                  whichever audience you pick. That is deliberate. "Last seen on the island"
                  is blank for people who never gave the app their location, and it goes
                  stale for anyone who has not opened the app in 14 days, and those people
                  can be standing right in the storm's path. Showing a warning to a
                  Dominican living abroad costs them one banner they can close. Not showing
                  it to someone in the path is the mistake that hurts, so the banner errs
                  the other way on purpose.
                </p>
              </div>

              {outsideOwnAudience ? (
                <div className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
                  <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                    <BellOff className="w-3.5 h-3.5" />
                    You will not get this push yourself.
                  </p>
                  <p className="text-[11px] text-amber-800/90 dark:text-amber-300/90 mt-1.5 leading-relaxed">
                    Your own phone was not last seen on Dominica
                    {lastSeenOnIsland ? ` (last seen there ${lastSeenOnIsland})` : ''}, so you
                    are outside the On&nbsp;Dominica audience and this alert will not reach
                    you. People on the island still get it, and your silence is not evidence
                    that anything failed. To prove pushes reach your own phone, use
                    "Send a test to me" on the right.
                  </p>
                </div>
              ) : null}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-500">3 · Write the alert</p>
                <div className="flex flex-wrap gap-1.5">
                  {TEMPLATES.map((t) => (
                    <button
                      key={t.label}
                      onClick={() => {
                        setSeverity(t.severity);
                        setTitle(t.title);
                        setBody(t.body);
                      }}
                      className="px-2 py-0.5 rounded-full text-[11px] border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-500"
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <div>
                  <Input
                    placeholder="Alert title (what happened)"
                    value={title}
                    maxLength={TITLE_MAX}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                  <p className="text-[11px] text-gray-400 mt-1 text-right">
                    {title.length}/{TITLE_MAX}
                  </p>
                </div>
                <div>
                  <Textarea
                    ref={bodyRef}
                    placeholder="Message (what people should do)"
                    value={body}
                    maxLength={BODY_MAX}
                    rows={4}
                    onChange={(e) => setBody(e.target.value)}
                  />
                  <div className="flex items-center justify-between mt-1">
                    <button
                      type="button"
                      onClick={() => setEmojiOpen((o) => !o)}
                      className={`flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
                        emojiOpen
                          ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
                          : 'text-gray-500 border-gray-300 dark:border-gray-700 hover:border-gray-500'
                      }`}
                    >
                      <SmilePlus className="w-3.5 h-3.5" />
                      Emoji
                    </button>
                    <p className="text-[11px] text-gray-400">
                      {body.length}/{BODY_MAX}
                    </p>
                  </div>
                  {emojiOpen ? (
                    <div className="mt-2 rounded-lg border p-3 space-y-2 bg-gray-50 dark:bg-gray-900/50">
                      {EMOJI_GROUPS.map((g) => (
                        <div key={g.label}>
                          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
                            {g.label}
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {g.emojis.map((e) => (
                              <button
                                key={e}
                                type="button"
                                onClick={() => insertEmoji(e)}
                                className="w-8 h-8 rounded-md text-lg hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
                                aria-label={`Insert ${e}`}
                              >
                                {e}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                      <p className="text-[10px] text-gray-400 pt-1">
                        Emojis insert at your cursor in the message. Tip: your keyboard's own
                        emoji picker works too (Mac: Ctrl+Cmd+Space · Windows: Win+.), including
                        in the title field.
                      </p>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 mb-2">
                4 · Keep the in-app banner up for
              </p>
              <div className="flex gap-2">
                {[6, 12, 24, 48].map((h) => (
                  <button
                    key={h}
                    onClick={() => setTtlHours(h)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                      ttlHours === h
                        ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
                        : 'bg-gray-100 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-700 hover:border-gray-500'
                    }`}
                  >
                    {h}h
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-gray-400 mt-1">
                The push goes out once, right away. The in-app banner stays visible for
                this long (or until you cancel the alert).
              </p>
            </div>

            <div className="pt-1">
              <Button
                className={severity === 'emergency' ? 'bg-red-600 hover:bg-red-700' : ''}
                disabled={!canSend}
                onClick={() => setConfirmOpen(true)}
              >
                <Send className="w-4 h-4 mr-1.5" />
                Review &amp; send
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* ── Live preview + self-test ── */}
        <div className="space-y-3 lg:sticky lg:top-6">
          <p className="text-xs font-semibold text-gray-500">Live preview</p>
          <PushPreview severity={severity} title={title} body={body} />

          <Card>
            <CardContent className="p-4 space-y-2">
              <p className="text-xs font-semibold flex items-center gap-1.5">
                <FlaskConical className="w-3.5 h-3.5" />
                Check that pushes reach your phone
              </p>
              <p className="text-[11px] text-gray-500 leading-relaxed">
                Sends one notification to your own device and nowhere else. It ignores the
                audience above, so it works from anywhere in the world. It is clearly marked
                as a test, goes out on the quiet Island Pulse channel so it can never wake
                anyone through Do Not Disturb, and is not recorded as a sent alert. If the
                title and message are filled in, it uses your draft copy.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                disabled={testing}
                onClick={() => void sendSelfTest()}
                aria-label="Send a test island alert to my own device only"
              >
                <FlaskConical className="w-3.5 h-3.5 mr-1.5" />
                {testing ? 'Sending test…' : 'Send a test to me'}
              </Button>
              {selfStatus ? (
                <div className="text-[11px] leading-relaxed pt-0.5">
                  {selfStatus.has_push_token ? (
                    <p className="text-gray-500">
                      Your account has a push token registered. Your phone was{' '}
                      {selfStatus.on_island
                        ? `last seen on Dominica${lastSeenOnIsland ? ` ${lastSeenOnIsland}` : ''}`
                        : 'not last seen on Dominica'}
                      .
                      {selfStatus.last_test_at
                        ? ` Last test ${formatDistanceToNow(new Date(selfStatus.last_test_at), { addSuffix: true })}.`
                        : ''}
                    </p>
                  ) : (
                    <p className="text-amber-700 dark:text-amber-400">
                      No push token registered for your account. Open the H-Plate app on your
                      phone, sign in with this same account and allow notifications.
                      Otherwise no push, test or real, can reach you.
                    </p>
                  )}
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ── Confirm dialog ── */}
      <Dialog
        open={confirmOpen}
        onOpenChange={(open) => {
          setConfirmOpen(open);
          if (!open) setConfirmText('');
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {severity === 'emergency' ? (
                <Siren className="w-5 h-5 text-red-500" />
              ) : (
                <Megaphone className="w-5 h-5 text-amber-500" />
              )}
              Send this {severity === 'info' ? 'notice' : severity} to {audienceLabel}?
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 pt-2">
                <div className="rounded-lg border p-3 bg-gray-50 dark:bg-gray-900">
                  <p className="text-sm font-bold text-gray-900 dark:text-gray-100">
                    {severity === 'emergency' ? '🚨 ' : severity === 'advisory' ? '⚠️ ' : ''}
                    {title.trim()}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 whitespace-pre-wrap">
                    {body.trim()}
                  </p>
                </div>
                <p className="text-xs text-gray-500">
                  This cannot be un-sent once pushes are delivered. You can cancel the
                  alert afterwards to hide the in-app banner.
                </p>
                {outsideOwnAudience ? (
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    You will not receive this push yourself. Your phone was not last seen
                    on Dominica, so you are outside this audience. That is expected and does
                    not mean the send failed.
                  </p>
                ) : null}
                {needsTypedConfirm ? (
                  <div>
                    <p className="text-xs font-semibold text-red-600 mb-1.5">
                      Emergency alerts wake people at any hour. Type SEND to confirm.
                    </p>
                    <Input
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder="Type SEND"
                      autoFocus
                    />
                  </div>
                ) : null}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={sending}>
              Back
            </Button>
            <Button
              className={severity === 'emergency' ? 'bg-red-600 hover:bg-red-700' : ''}
              disabled={sending || (needsTypedConfirm && confirmText.trim().toUpperCase() !== 'SEND')}
              onClick={() => void doSend()}
            >
              {sending ? 'Sending…' : 'Send alert now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── History ── */}
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <p className="text-sm font-semibold">Sent alerts</p>
            <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
              Refresh
            </Button>
          </div>
          {loading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-gray-500">
              <Megaphone className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No alerts sent yet.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Alert</TableHead>
                  <TableHead>Audience</TableHead>
                  <TableHead>Reached</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const active = !r.canceled_at && new Date(r.expires_at).getTime() > Date.now();
                  return (
                    <TableRow key={r.id}>
                      <TableCell>{statusOf(r)}</TableCell>
                      <TableCell>{severityBadge(r.severity)}</TableCell>
                      <TableCell className="max-w-md">
                        <p className="text-sm font-medium">{r.title}</p>
                        <p className="text-xs text-gray-500 line-clamp-2">{r.body}</p>
                        {r.sent_by_name ? (
                          <p className="text-[11px] text-gray-400 mt-0.5">by {r.sent_by_name}</p>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-sm">
                        {r.audience === 'on_island' ? 'On Dominica' : 'Everyone'}
                      </TableCell>
                      <TableCell className="text-sm">{r.push_recipients}</TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => reuseAlert(r)}
                            title="Load this alert into the composer to edit and send as a new alert"
                          >
                            <RotateCcw className="w-3.5 h-3.5 mr-1" />
                            Reuse
                          </Button>
                          {active ? (
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => void cancelAlert(r.id)}
                              disabled={busyId === r.id}
                            >
                              <Ban className="w-3.5 h-3.5 mr-1" />
                              Cancel
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
